import { createSelector } from '@reduxjs/toolkit';
import { CONSTANTS_SETTINGS } from 'config/settingsContainer';
import { STAFF_USER_TYPES, USER_TYPE } from 'config/userType';
import { INVITE_STATUS } from 'library/helpers/personStatus';
import { rolesConfig } from 'config/rolesConfig';
import { filterPeopleByFilters } from 'library/helpers/bookingHelpers';
import { selectLockerLogs } from 'reduxStore/accessLogs/lockerSelectors';
import { selectRoles } from 'reduxStore/roles/selector';
import { selectSites } from 'reduxStore/sites/selectors';
import { selectCurrentProfileOrgSettings } from 'reduxStore/settings/selectors';
import { selectHiddenHolderPersonIDs } from 'reduxStore/roomStays/selectors';

// Base input selectors for scoped people data: state.People.data[orgID][siteID].data
const selectPeopleData = (state) => state.People.data;
const selectOrgID = (state) => state.Auth.selectedProfile.orgID;
const selectSiteID = (state) => state.Auth.selectedProfile.siteID;

const nameSort = (a, b) =>
	(a.displayName || a.firstName || '').localeCompare(b.displayName || b.firstName || '', undefined, {
		sensitivity: 'base'
	});

//📌 Selector: all people for the active scope (org + site) — sorted A-Z by name, card holders INCLUDED.
// 🛑 Only for what must still reach a room holder: Manage Access targets, name lookups, the "Show card holders"
// toggle (room-cards-setup §5.1.2). Every list, picker and count reads `selectPeople` below.
export const selectPeopleIncludingHolders = createSelector(
	[selectPeopleData, selectOrgID, selectSiteID],
	(peopleData, orgID, siteID) => {
		const key = siteID;
		return [...(peopleData[orgID]?.[key]?.data || [])].sort(nameSort);
	}
);

/** Drops room / Card Stock / Card Store holders while credentials.roomStays is on; passthrough while it is off. */
const withoutHolders = (people, hidden) => (hidden.size ? people.filter((p) => !hidden.has(p.personID)) : people);

//📌 Selector: all people for the active scope (org + site) — sorted A-Z by name, card holders hidden (R6, INV-06)
export const selectPeople = createSelector(
	[selectPeopleIncludingHolders, selectHiddenHolderPersonIDs],
	withoutHolders,
	{ devModeChecks: { identityFunctionCheck: 'never' } }
);

// ONE definition of "is a Zezamii (staff) user" — both selectors below filter on it, so a future
// change to what counts as staff (e.g. also excluding a deactivated status) has one call site.
const isZezamiiUser = (person) => person.userTypeID === USER_TYPE.ZEZAMII_USER;

//📌 Selector: only Zezamii users (userTypeID === ZEZAMII_USER)
export const selectZezamiiUsers = createSelector([selectPeople], (people) => people.filter(isZezamiiUser));

//📌 Selector: only Visitor users (userTypeID === VISITOR) — shown in Visitors page
export const selectVisitorUsers = createSelector([selectPeople], (people) =>
	people.filter((person) => person.userTypeID === USER_TYPE.VISITOR)
);

//📌 Selector: users that are neither Zezamii nor Visitor (legacy types still awaiting migration)
export const selectOtherUsers = createSelector([selectPeople], (people) =>
	people.filter((person) => person.userTypeID !== USER_TYPE.ZEZAMII_USER && person.userTypeID !== USER_TYPE.VISITOR)
);

//📌 Selector: the People page rows (staff, then other types) WITH card holders — the "Show card holders" toggle
export const selectZezamiiAndOtherUsersIncludingHolders = createSelector([selectPeopleIncludingHolders], (people) => [
	...people.filter(isZezamiiUser),
	...people.filter((person) => !isZezamiiUser(person) && person.userTypeID !== USER_TYPE.VISITOR)
]);

//📌 Selector: people who may join a team (staff only — visitors never do). Shared by every add-to-team picker.
export const selectTeamEligiblePeople = createSelector([selectPeople], (people) =>
	people.filter((person) => STAFF_USER_TYPES.includes(person.userTypeID))
);

//📌 Selector: loading state for the active scope bucket
export const selectPeopleLoading = createSelector(
	[selectPeopleData, selectOrgID, selectSiteID],
	(peopleData, orgID, siteID) => {
		const key = siteID;
		return peopleData[orgID]?.[key]?.loading ?? false;
	}
);

//📌 Selector for login-capable people based on Inner Range setting
export const selectLoginCapablePeople = createSelector(
	[selectPeople, selectCurrentProfileOrgSettings],
	(people, orgSettings) => {
		const innerRangeEnabled = orgSettings.some(
			(setting) =>
				setting.defaultSettingID === CONSTANTS_SETTINGS.INNER_RANGE_MODULE && Number(setting.value) === 1
		);

		if (innerRangeEnabled) {
			return people;
		}

		return people.filter((person) => person.userTypeID !== USER_TYPE.ACCESS_ONLY_USER);
	},
	// Returns `people` verbatim when InnerRange is enabled — reselect's
	// identity check warns on the first invocation in that org config.
	{ devModeChecks: { identityFunctionCheck: 'never' } }
);

// 🧠 SELECTOR: Person + Admin Roles
export const selectPeopleWithAdminRoles = createSelector(
	[selectPeople, selectRoles, selectSites],
	(people, roles, sites) => {
		return people.map((person) => {
			// 🔥 Get roles for this person
			const personRoles = roles.filter((role) => role.personID === person.personID);

			// 🔥 Filter by permission type
			const organisationRoles = personRoles
				.filter((role) => role.permissionID === rolesConfig.GLOBAL_ADMIN && role.entityTypeID === 1)
				.map((role) => role.entityID);

			const siteRoles = personRoles
				.filter((role) => role.permissionID === rolesConfig.SITE_ADMIN && role.entityTypeID === 2)
				.map((role) => {
					// 🔥 Resolve the site name
					const siteInfo = sites.find((site) => site.siteID === role.entityID);
					return {
						siteID: role.entityID,
						displayName: siteInfo?.name || '-'
					};
				});

			return {
				...person,
				admin: {
					organisation: organisationRoles,
					sites: siteRoles
				}
			};
		});
	}
);

//📌 Selector: people + loading for the active scope
export const selectPeopleAndLoading = createSelector([selectPeople, selectPeopleLoading], (people, loading) => ({
	people,
	loading
}));

//📌 Selector: person by personID — searches active bucket, falls back to 'all'
export const selectPeopleByPersonID = createSelector(
	[selectPeopleData, selectOrgID, selectSiteID, (_, personID) => personID],
	(peopleData, orgID, siteID, personID) => {
		const key = siteID;
		// Search active bucket first
		const found = peopleData[orgID]?.[key]?.data?.find((p) => p.personID === personID);
		if (found) return found;
		// Fallback: search 'all' bucket (exists if user visited portfolio mode)
		if (key !== 'all') {
			return peopleData[orgID]?.['all']?.data?.find((p) => p.personID === personID) || null;
		}
		return null;
	}
);

//📌 Selector: dependant people by personID
export const selectDependantPeopleByPersonID = createSelector(
	[selectPeople, (_, personID) => personID],
	(people, personID) => people.filter((person) => person.masterUserID === personID) || []
);

//📌 Selector: people by siteID (explicit site filter, not scope-based)
// null/undefined/'all' → passthrough (all people). Specific siteID → filter.
export const selectPeopleBySiteID = createSelector(
	[selectPeople, (_, siteID) => siteID],
	(people, siteID) => {
		if (siteID === 'all') return people;
		return people.filter((person) => person.siteID === siteID || person.sites?.some((s) => s.siteID === siteID));
	},
	{ devModeChecks: { identityFunctionCheck: 'never' } }
);

// 📌 Selector: Active Users
export const selectActiveUsers = createSelector([selectPeople], (people) => {
	return people.filter((person) => person.inviteStatus === INVITE_STATUS.ACCEPTED).length;
});

// 📌 Selector: Inactive Users
export const selectInactiveUsers = createSelector([selectPeople], (people) => {
	return (
		people.filter((person) => person.inviteStatus !== INVITE_STATUS.ACCEPTED).length
	);
});

// 📌 Selector: Get Data for Pie Chart
export const selectActiveAndInactiveCounts = createSelector(
	[selectActiveUsers, selectInactiveUsers],
	(activeCount, inactiveCount) => [
		{ name: 'Active', value: activeCount },
		{ name: 'Inactive', value: inactiveCount }
	]
);

// One person-search predicate for both search selectors below — matches any
// of the name fields (name, first+last, displayName) or email
const personMatchesSearch = (person, needle) =>
	[person.name, `${person.firstName} ${person.lastName}`, person.displayName, person.email].some((field) =>
		field?.toLowerCase().includes(needle)
	);

//📌 Selector: people by name search
export const selectPeopleByName = createSelector(
	[selectPeople, (_, name) => name],
	(people, name) => people.filter((person) => personMatchesSearch(person, name.toLowerCase())) || []
);

//📌 Selector: people by filters (Lockers dashboard — reads from 'all' bucket)
export const selectPeopleByFilters = createSelector(
	[
		selectPeopleData,
		(state) => state.Bookings.data,
		// Locker-scoped: the window itself is org-wide, and counting a person's door
		// swipes made 'Frequent user' and 'New user' answer the wrong question.
		selectLockerLogs,
		selectOrgID,
		selectPeopleLoading,
		selectHiddenHolderPersonIDs,
		(state, searchTerm) => searchTerm,
		(state, searchTerm, selectedFilters) => selectedFilters
	],
	(peopleData, bookingsData, orgLogs, orgID, loading, hidden, searchTerm, selectedFilters) => {
		// Lockers always reads from 'all' bucket
		const orgPeople = withoutHolders(peopleData[orgID]?.['all']?.data || [], hidden);
		const orgBookings = bookingsData[orgID] || [];

		// 🔍 Apply search filter
		let filteredPeople = orgPeople;
		if (searchTerm) {
			const needle = searchTerm.toLowerCase();
			filteredPeople = filteredPeople.filter((person) => personMatchesSearch(person, needle));
		}

		// 🎯 Apply active filters
		const finalPeople = filterPeopleByFilters(filteredPeople, orgBookings, orgLogs, selectedFilters);

		return {
			people: finalPeople,
			bookingsData: orgBookings,
			logsData: orgLogs,
			loading
		};
	}
);

//📌 Selector: selected users for invitation — a STABLE empty array, since a fresh `[]` re-renders
// the invite modal on every store tick.
const NO_SELECTED_USERS = Object.freeze([]);

//📌 Emails staged in the invite dialog. Never `undefined`: consumers read `.length` unguarded and an
// absent slice once crashed the Lockers topbar.
export const selectSelectedUsers = createSelector(
	[(state) => state.People?.selectedUsers],
	(selectedUsers) => selectedUsers || NO_SELECTED_USERS
);

//📌 Selector: contact by personID
export const selectContactByPersonID = createSelector(
	[selectPeopleData, selectOrgID, selectSiteID, (_, personID) => personID],
	(peopleData, orgID, siteID, personID) => {
		const key = siteID;
		const person = peopleData[orgID]?.[key]?.data?.find((p) => p.personID === personID);
		if (person?.contacts?.data?.[0]) return person.contacts.data[0];
		// Fallback to 'all' bucket
		if (key !== 'all') {
			const fallback = peopleData[orgID]?.['all']?.data?.find((p) => p.personID === personID);
			return fallback?.contacts?.data?.[0] || null;
		}
		return null;
	}
);

//📌 Selector: Lockers — always reads org-wide people from 'all' bucket
export const selectLockersAllPeople = createSelector(
	[selectPeopleData, selectOrgID, selectHiddenHolderPersonIDs],
	(peopleData, orgID, hidden) => withoutHolders(peopleData[orgID]?.['all']?.data || [], hidden),
	{ devModeChecks: { identityFunctionCheck: 'never' } }
);

// Zezamii users across the WHOLE org (hosts can come from any site). 🛑 Access never pre-fetches the
// 'all' bucket — callers must load it themselves (see selectZezamiiUsersAllSitesLoad / NewVisitModal).
export const selectZezamiiUsersAllSites = createSelector([selectLockersAllPeople], (people) =>
	people.filter(isZezamiiUser)
);

// Active, name-sorted staff for host/assignee pickers. Eligibility lives here; the option shape
// stays in the component.
export const selectActiveZezamiiUsersAllSitesSorted = createSelector([selectZezamiiUsersAllSites], (people) =>
	[...people]
		.filter((p) => p.isActive !== false)
		.sort((a, b) =>
			(a.displayName || a.firstName || '').localeCompare(b.displayName || b.firstName || '', undefined, {
				sensitivity: 'base'
			})
		)
);

//📌 Selector: loading state of the 'all' bucket selectLockersAllPeople reads
export const selectLockersAllPeopleLoading = createSelector(
	[selectPeopleData, selectOrgID],
	(peopleData, orgID) => peopleData[orgID]?.['all']?.loading ?? false
);

// Has the 'all' bucket EVER been fetched? Tells "not loaded" apart from "zero staff" (both read `[]`),
// so NewVisitModal knows whether to fetch it.
export const selectLockersAllPeopleFetched = createSelector(
	[selectPeopleData, selectOrgID],
	(peopleData, orgID) => peopleData[orgID]?.['all'] !== undefined
);

//📌 Selector: Map<personID, displayName> for O(1) name lookups
// Holders INCLUDED: a room's name must still resolve wherever a card or log names it (§5.1.2 "Kept").
export const selectPeopleNameMap = createSelector([selectPeopleIncludingHolders], (people) => {
	const map = new Map();
	people.forEach((p) => map.set(p.personID, p.displayName || 'Unknown'));
	return map;
});

// Bulk Add People run — `progress` is null until the first run of the session.
const EMPTY_BULK_ADD = { running: false, cancelRequested: false, progress: null };
export const selectBulkAddPeople = (state) => state.People?.bulkAdd ?? EMPTY_BULK_ADD;

// True while the signed-in person's account deletion request is being sent.
export const selectAccountDeletionRequestLoading = (state) => state.People.loadingAccountDeletionRequest;

// True while a person's profile fields are being saved.
export const selectUpdatePersonLoading = (state) => state.People.loadingUpdateProfile;

// True while a person's contact is loaded or saved (add and update use separate flags).
export const selectPersonContactLoading = (state) => state.People.loadingContacts || state.People.loadingUpdateContact;
